import { withTrailingSlash } from 'ufo'
import type { Product } from 'schema-dts'
import type { MaybeRefOrGetter } from 'vue'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'
import { organizationReference } from '~/composables/schemaOrg/useOrganizationSchema'

/**
 * ItemList of the special giftcards (/giftcard/{slug}/) shown on the giftcard
 * page, for Google's host carousel (beta). Each node shares its @id, name,
 * category, brand and offer with the Product on its detail page
 * (useProductSchema), so Google reads them as the same product.
 */
export const useGiftcardItemListSchema = (
  giftcards: MaybeRefOrGetter<SearchEngineEventItem[] | null | undefined>
) => {
  const localePath = useLocalePath()
  const {
    public: { baseUrl },
  } = useRuntimeConfig()
  // Not useFullLocalizedUrl: its resolver calls useSubdomain(), which fails
  // inside the useHead callback.
  const giftcardUrl = (permalink: string) =>
    withTrailingSlash(
      new URL(localePath(`/giftcard/${permalink}/`), baseUrl).toString(),
      true
    )

  useHead(() => {
    const nodes = (toValue(giftcards) ?? []).map((giftcard): Product => {
      const url = giftcardUrl(searchEngineEventGetters.fnGetPermalink(giftcard))
      const price =
        searchEngineEventGetters.fnGetMinimumPriceAmount(giftcard) / 100
      const image = searchEngineEventGetters.getPrincipalImage(giftcard)

      return {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: searchEngineEventGetters.fnGetTitle(giftcard),
        url,
        ...(image && { image: [toKonfettiWidth(image, 1200)] }),
        description: searchEngineEventGetters.fnGetSubtitle(giftcard),
        category: 'Gift Card',
        brand: organizationReference,
        ...(price > 0 && { offers: getGiftcardOffer({ price, url }) }),
      }
    })
    const itemList = getSchemaItemList(nodes)
    if (!itemList) return {}

    return {
      script: [
        {
          key: 'giftcard-item-list-schema',
          type: 'application/ld+json',
          innerHTML: JSON.stringify(itemList),
        },
      ],
    }
  })
}
